<script setup lang="ts">
import { TextField } from '@haskoning/vue-component-library'
import MaterialEditor from '@/components/building-block-form/MaterialEditor.vue'
import ProductSearchDialog from '@/components/app/ProductSearchDialog.vue'
import BuildingBlockSwitchIssueDetails from '@/components/building-block-form/BuildingBlockSwitchIssueDetails.vue'
import type { BuildingBlockCardProps } from '@/components/building-block-form/type/type'
import { useBuildingBlockCard } from '@/components/building-block-form/composable/useBuildingBlockCard'

const props = defineProps<BuildingBlockCardProps>()
const {
  materialItems,
  isEquipmentBlock,
  impactLabel,
  autosaveMessage,
  autosaveMessageColor,
  blockTotal,
  issueClass,
  issueBadgeColor,
  issueTitle,
  isEditLockedByMissingIssue,
  missingDetails,
  changedDetails,
  showAddProduct,
  blockTypeCopy,
  buildingBlockNameField,
  buildingBlockUnitField,
  editingBlock,
  onNameTyping,
  onUnitTyping,
  onAddProductSelected,
  onAddEquipmentRow,
  onDuplicateBlock,
  onDeleteBlock,
  canDeleteMaterial
} = useBuildingBlockCard(props)
</script>

<template>
  <v-expansion-panel :class="['building-block-card', issueClass]">
    <v-expansion-panel-title>
      <template #default="{ expanded }">
        <v-icon class="mr-2">
          {{ expanded ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
        </v-icon>
        <v-icon start class="mr-2">mdi-copyright</v-icon>
        <div class="d-flex flex-column">
          <span class="font-weight-medium">
            Building block – {{ props.block.name }}
            <span class="text-medium-emphasis font-weight-regular ml-2"
              >{{ impactLabel }}: € {{ blockTotal.toFixed(2) }} (total)</span
            >
            <v-chip
              v-if="autosaveMessage"
              class="ml-2"
              :color="autosaveMessageColor"
              size="small"
              variant="tonal"
            >
              {{ autosaveMessage }}
            </v-chip>
          </span>
          <BuildingBlockSwitchIssueDetails
            v-if="props.switchValidationIssue"
            :issue-title="issueTitle"
            :issue-badge-color="issueBadgeColor"
            :missing-details="missingDetails"
            :changed-details="changedDetails"
          />
        </div>
      </template>
      <template #actions="">
        <div class="d-flex gap-1 ml-2">
          <v-btn
            icon="mdi-content-duplicate"
            variant="text"
            size="small"
            :disabled="props.readonly || isEditLockedByMissingIssue"
            @click.stop="onDuplicateBlock"
          />
          <v-btn
            icon="mdi-delete"
            variant="text"
            size="small"
            :disabled="props.readonly"
            @click.stop="onDeleteBlock"
          />
        </div>
      </template>
    </v-expansion-panel-title>

    <v-expansion-panel-text>
      <v-row>
        <v-col cols="12" sm="9" md="9" class="pa-3">
          <TextField
            :field="buildingBlockNameField"
            :model-value="editingBlock"
            density="comfortable"
            :no-help-text="true"
            hide-details
            class="pb-2"
            :readonly="props.readonly || isEditLockedByMissingIssue"
            @input="onNameTyping"
          />
        </v-col>
        <v-col cols="12" sm="3" md="3" class="pa-3">
          <TextField
            :field="buildingBlockUnitField"
            :model-value="editingBlock"
            density="comfortable"
            :no-help-text="true"
            hide-details
            class="pb-2"
            :readonly="props.readonly || isEditLockedByMissingIssue"
            @input="onUnitTyping"
          />
        </v-col>
      </v-row>
      <div v-for="material in materialItems" :key="material.id" class="mb-1">
        <MaterialEditor
          v-if="material.block === props.block.id"
          :material="material"
          :is-equipment-block="isEquipmentBlock"
          :row-label="blockTypeCopy.rowLabel"
          :row-impact-label="blockTypeCopy.rowImpactLabel"
          :name-field-label="blockTypeCopy.nameFieldLabel"
          :readonly="props.readonly || isEditLockedByMissingIssue"
          :can-delete="canDeleteMaterial(material.id)"
        />
      </div>

      <v-btn
        class="mt-2"
        color="primary"
        prepend-icon="mdi-plus"
        :disabled="props.readonly || isEditLockedByMissingIssue"
        @click="isEquipmentBlock ? onAddEquipmentRow() : (showAddProduct = true)"
      >
        {{ blockTypeCopy.addButtonLabel }}
      </v-btn>

      <ProductSearchDialog
        v-if="!isEquipmentBlock && blockTypeCopy.addProductDialog"
        v-model="showAddProduct"
        :readonly="props.readonly || isEditLockedByMissingIssue"
        :title="blockTypeCopy.addProductDialog.title"
        :subtitle="blockTypeCopy.addProductDialog.subtitle"
        :search-label="blockTypeCopy.addProductDialog.searchLabel"
        :no-match-label="blockTypeCopy.addProductDialog.noMatchLabel"
        :empty-search-label="blockTypeCopy.addProductDialog.emptySearchLabel"
        :confirm-text="blockTypeCopy.addProductDialog.addButtonLabel"
        @select="onAddProductSelected"
      />
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>

<style scoped>
.building-block-card {
  border: 1px solid #c0c0c0;
  border-radius: 10px;
}

.building-block-card-missing {
  border: 1px solid rgb(var(--v-theme-error));
  background-color: rgba(var(--v-theme-error), 0.08);
}

.building-block-card-changed {
  border: 1px solid rgb(var(--v-theme-warning));
  background-color: rgba(var(--v-theme-warning), 0.12);
}
</style>
